import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertTriangle, CheckCircle2, Gift, UtensilsCrossed } from 'lucide-react';
import useStore from '@/store/useStore';
import { apiRedeem, apiGetWallet, apiGetTransactions, apiGetRedemptionStatus } from '@/services/api';

export default function RedemptionModal() {
  const {
    showRedeemModal,
    redeemModalData,
    closeRedeemModal,
    setWallet,
    setTransactions,
    merchantInfo,
  } = useStore();
  const [loading, setLoading] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const [redemptionId, setRedemptionId] = useState(null);
  const [redemptionStatus, setRedemptionStatus] = useState('idle');
  const [redemptionChannel, setRedemptionChannel] = useState('source');
  const [redeemPointsInput, setRedeemPointsInput] = useState('');
  const [campaignCodeInput, setCampaignCodeInput] = useState('');
  const [redeemError, setRedeemError] = useState('');

  const refreshGuestData = useCallback(async () => {
    try {
      const [w, tx] = await Promise.all([apiGetWallet(), apiGetTransactions()]);
      setWallet(w);
      setTransactions(tx);
    } catch (e) {}
  }, [setTransactions, setWallet]);

  const resetState = useCallback(() => {
    setRedeemed(false);
    setRedemptionId(null);
    setRedemptionStatus('idle');
    setRedemptionChannel('source');
    setRedeemPointsInput('');
    setCampaignCodeInput('');
    setRedeemError('');
  }, []);

  const handleClose = useCallback(async (shouldRefresh = true) => {
    closeRedeemModal();
    resetState();
    if (shouldRefresh) {
      await refreshGuestData();
    }
  }, [closeRedeemModal, refreshGuestData, resetState]);

  const handleRedeem = useCallback(async () => {
    const availablePoints = Number(redeemModalData?.points || 0);
    const redeemPoints = Number(redeemPointsInput);
    const promoCode = campaignCodeInput.trim().toUpperCase();
    const promoOnlyMode = Boolean(promoCode);

    if (!promoOnlyMode) {
      if (!Number.isFinite(redeemPoints) || redeemPoints <= 0) {
        setRedeemError('Please enter a valid points amount');
        return;
      }
      if (redeemPoints < 100) {
        setRedeemError('Minimum 100 points to redeem');
        return;
      }
      if (redeemPoints > availablePoints) {
        setRedeemError('Entered points exceed available balance');
        return;
      }
    }

    setLoading(true);
    setRedeemError('');
    try {
      const result = await apiRedeem(promoOnlyMode ? 0 : redeemPoints, 'source', null, promoOnlyMode ? promoCode : null);
      setRedemptionId(result.redemption_id);
      setRedemptionStatus('pending');
      setRedemptionChannel(String(result?.channel || (promoOnlyMode ? 'promo' : 'source')));
      setRedeemed(true);
    } catch (e) {
      setRedeemError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Unable to place redemption request');
    }
    setLoading(false);
  }, [campaignCodeInput, redeemModalData, redeemPointsInput]);

  useEffect(() => {
    if (!showRedeemModal || !redemptionId || redemptionStatus !== 'pending') return;

    const interval = setInterval(async () => {
      try {
        const status = await apiGetRedemptionStatus(redemptionId);
        if (status?.channel) {
          setRedemptionChannel(String(status.channel));
        }
        if (status.status === 'validated') {
          setRedemptionStatus('validated');
          await refreshGuestData();
        } else if (status.status === 'rejected') {
          setRedemptionStatus('rejected');
          setRedeemError(
            typeof status?.rejected_reason === 'string' && status.rejected_reason.trim()
              ? status.rejected_reason.trim()
              : 'Merchant rejected this redemption request.'
          );
        }
      } catch (e) {}
    }, 2000);

    return () => clearInterval(interval);
  }, [showRedeemModal, redemptionId, redemptionStatus, refreshGuestData]);

  useEffect(() => {
    if (!showRedeemModal) return;
    if (redemptionStatus !== 'validated') return;

    const t = setTimeout(() => {
      handleClose(false);
    }, 2200);

    return () => clearTimeout(t);
  }, [redemptionStatus, showRedeemModal, handleClose]);

  return (
    <Dialog open={showRedeemModal} onOpenChange={() => handleClose(true)}>
      <DialogContent className={`max-w-sm mx-auto p-0 overflow-hidden border-0 ${redeemed ? 'bg-stone-900 text-white' : 'rounded-2xl'}`} data-testid="redemption-modal">
        {!redeemed ? (
          <div className="p-6 text-center space-y-5">
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>Redeem Rewards</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">Convert your points to rewards</DialogDescription>
            <div className="rounded-xl border border-border p-4 space-y-2">
              <p className="text-sm text-muted-foreground">Available Points</p>
              <p className="text-3xl font-black" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>{redeemModalData?.points?.toLocaleString() || 0}</p>
              <p className="text-sm text-brand">${((redeemModalData?.points || 0) / 100).toFixed(2)}</p>
            </div>
            <div className="rounded-xl border border-border p-4 space-y-2 text-left">
              <label className="text-sm font-semibold">Points to Redeem</label>
              <input
                data-testid="redeem-points-input"
                type="number"
                min={100}
                max={redeemModalData?.points || 0}
                step={1}
                value={redeemPointsInput}
                onChange={(e) => setRedeemPointsInput(e.target.value.replace(/[^\d]/g, ''))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleRedeem();
                  }
                }}
                placeholder="Enter points (min 100)"
                className="w-full h-11 rounded-xl border border-input px-3 bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-muted-foreground">
                You can redeem from 100 up to {(redeemModalData?.points || 0).toLocaleString()} points.
              </p>
              <label className="text-sm font-semibold pt-1 block">Campaign Code (Optional)</label>
              <input
                data-testid="redeem-campaign-code-input"
                type="text"
                value={campaignCodeInput}
                onChange={(e) => setCampaignCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                placeholder="e.g. COMEBACKA1B2"
                className="w-full h-11 rounded-xl border border-input px-3 bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-muted-foreground">
                If promo code is valid, points redemption is skipped automatically.
              </p>
              {redeemError && <p className="text-xs text-destructive">{redeemError}</p>}
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="font-semibold text-foreground">Quick Rules</p>
              <p>Earn 5 pts per $1 on every eligible order (pre-discount subtotal).</p>
              <p>Visit bonuses auto-apply: Visit 1 (200 pts on $15+), Visit 2 (2x within 7 days), Visit 3 (+100 pts).</p>
              <p>Redeem from 100 points. Per-check limits apply and are shown in points before you confirm.</p>
            </div>
            <button
              data-testid="confirm-redeem-btn"
              onClick={handleRedeem}
              disabled={loading || ((redeemModalData?.points || 0) < 100 && !campaignCodeInput.trim())}
              className="w-full h-12 rounded-full gradient-brand-btn text-base font-semibold disabled:opacity-50">
              {loading ? 'Processing...' : 'Confirm Redemption'}
            </button>
          </div>
        ) : redemptionStatus === 'validated' ? (
          <div className="p-8 bg-white text-stone-900 text-center space-y-4">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
            <h2 className="text-2xl font-bold" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>Redemption Complete</h2>
            <p className="text-sm text-stone-500">
              {redemptionChannel === 'promo'
                ? 'Promo code validated successfully. Your campaign savings have been applied to this bill.'
                : 'Your cashier has validated this reward. Your wallet balance has been updated.'}
            </p>
            <p className="text-xs text-stone-400">Closing automatically...</p>
          </div>
        ) : redemptionStatus === 'rejected' ? (
          <div className="p-8 bg-white text-stone-900 text-center space-y-4">
            <AlertTriangle className="w-14 h-14 text-rose-500 mx-auto" />
            <h2 className="text-2xl font-bold" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>Request Rejected</h2>
            <p className="text-sm text-stone-500">
              {redeemError || 'Merchant rejected this redemption request. Please try with different points or ask staff.'}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={resetState}
                className="h-11 rounded-xl border border-stone-200 text-sm font-semibold hover:bg-stone-50"
              >
                Try Again
              </button>
              <button
                type="button"
                onClick={() => handleClose(true)}
                className="h-11 rounded-xl gradient-brand-btn text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            {/* Dark header */}
            <div className="bg-stone-900 px-6 pt-4 pb-2 flex items-center justify-center">
              <span className="text-xs font-bold tracking-widest text-stone-400 uppercase">FlyBack</span>
            </div>
            {/* White card */}
            <div className="bg-white text-stone-900 rounded-t-3xl mx-4 mt-2 p-6 space-y-5">
              <div className="w-14 h-14 rounded-2xl gradient-brand flex items-center justify-center mx-auto">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>Redeem Rewards</h2>
                <p className="text-sm text-stone-500 mt-1">Your redemption request is sent. Merchant will validate it shortly.</p>
              </div>
              <div className="rounded-xl border border-brand/20 bg-brand-soft px-3 py-2 text-left">
                <p className="text-xs font-semibold text-brand">Request submitted successfully</p>
                <p className="text-[11px] text-stone-600 mt-1">
                  Your request is in queue. Merchant will approve or reject it from the dashboard.
                </p>
              </div>
              {/* Status */}
              <div className="flex items-center justify-center gap-2 bg-brand-soft text-brand text-xs font-bold uppercase tracking-wider py-2 px-4 rounded-full w-fit mx-auto">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--brand-solid)' }} /> Waiting For Validation
              </div>
            </div>
            {/* Restaurant footer */}
            <div className="bg-stone-800 mx-4 rounded-b-2xl p-3 flex items-center gap-3 mb-4">
              <UtensilsCrossed className="w-4 h-4 text-stone-400" />
              <div className="text-left">
                <p className="text-[10px] text-stone-500 uppercase tracking-wider">Redeeming at</p>
                <p className="text-sm font-semibold text-white">{merchantInfo?.restaurant_name || 'Restaurant'}</p>
              </div>
            </div>
            <p className="text-xs text-stone-500 pb-4">Need help? Contact support</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
